import { useApi } from "@/hooks";
import { BybButton, BybSwitch } from "@Components/common/byb-ds";
import { AppContext } from "@Context/common/general/context";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PlayPositions from "@Enums/PlayPositions";
import { Difficulties, PhaseOfPlayMap } from "@Enums/index";
import getDeterministicColor from "@Helpers/get-deterministic-color";
import ScenarioTemplate from "@Resources/game/scenario-templates/Template_433.json";
import { Api } from "@Shared/schemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { Autocomplete, Box, Chip, CircularProgress, MenuItem, Stack, TextField } from "@mui/material";
import { isNumber } from "lodash-es";
import { useContext, useEffect, useState } from "react";
import { ScenarioPositionsDialog } from ".";
import { BybDialog, BybSelectTextField, BybTextField } from "../common";
import { Formations, getCoordinatesForTeam, getPlayPositionString } from "./helpers";
import { Coordinates } from "./types";

interface IScenarioEditDialog {
  open: boolean;
  scenario?: ScenarioResponse;
  scenarioEditorData?: any;
  title?: string;
  buttonLabel?: string;
  onClose: () => void;
  onCreate?: (id : number) => void;
  onUpdate?: () => void;
}

const ScenarioEditDialog = ({ 
  open, 
  scenario, 
  scenarioEditorData, 
  title,
  buttonLabel,
  onClose, 
  onCreate, 
  onUpdate 
}: IScenarioEditDialog) => {
  const { state, dispatch } = useContext(AppContext);
  const [name, setName] = useState<string | undefined>(scenario?.name || "");
  const [formationYour, setFormationYour] = useState(Formations[442]);
  const [formationOpponent, setFormationOpponent] = useState(Formations[442]);
  const [description, setDescription] = useState<string | undefined>(scenario?.description || "");
  const [playPositions, setPlayPositions] = useState<PlayPositions[] | undefined>(scenario?.play_positions);
  const [playPositionSelectOpen, setPlayPositionSelectOpen] = useState(false);
  const [phaseOfPlay, setPhaseOfPlay] = useState<PhaseOfPlay>((scenario?.phase_of_play as PhaseOfPlay) || PhaseOfPlay.BUILD_UP);
  const [published, setPublished] = useState(scenario?.published || false);
  const [blueTeamCoords, setBlueTeamCoords] = useState<Coordinates>(getCoordinatesForTeam(Formations[442]));
  const [redTeamCoords, setRedTeamCoords] = useState<Coordinates>(getCoordinatesForTeam(Formations[442], "2"));
  const [tags, setTags] = useState<string[]>(scenario?.tags || []);

  const { call: createScenario, data: createScenarioResponse, loading: createScenarioLoading } = useApi<typeof Api.Scenarios.CreateScenario>({
    api: Api.Scenarios.CreateScenario,
    request: {
      account_id: state.account.id,
      name: name || "",
      description: description || "",
      play_positions: playPositions || [],
      difficulty: Difficulties.EASY,
      phase_of_play: phaseOfPlay,
      scenario_data: scenarioEditorData,
      pressure_time: 0,
      tags: tags || []
    },
    onSuccess: (response) => {
      handleClose();
      onCreate?.(response.data?.scenario_id);
    }
  });

  const { call: updateScenario, data: updateScenarioResponse, loading: updateScenarioLoading } = useApi<typeof Api.Scenarios.UpdateScenario>({
    api: Api.Scenarios.UpdateScenario,
    request: {
      scenario_id: scenario?.doc_id || "",
      name: name,
      description: description,
      play_positions: playPositions,
      phase_of_play: phaseOfPlay,
      difficulty: Difficulties.EASY,
      pressure_time: 0,
      scenario_data: scenarioEditorData || undefined,
      published: isNumber(published) ? (published === 1 ? true : false) : !!published,
      tags: tags
    },
    onSuccess: () => {
      handleClose();
      onUpdate?.();
    }
  });

  useEffect(() => {
    setName(scenario?.name || "");
    setDescription(scenario?.description || "");
    setPlayPositions(scenario?.play_positions);
    setPhaseOfPlay(scenario?.phase_of_play as PhaseOfPlay);
    setPublished(scenario?.published || false);
  }, [scenario]);

  const handleUpdate = () => {
    if (!scenario) return;
    if (scenarioEditorData) {
      scenarioEditorData.Name = name;
      console.log("Updated scenario with scenarioData", scenarioEditorData);
    }

    updateScenario();
  };

  const handleCreate = () => {

    let scenarioData = scenarioEditorData;
    if(!scenarioData) {
      console.log("Creating new scenario from template");
      scenarioData = { ...ScenarioTemplate };
      scenarioData.Players.forEach((player: any) => {
        const playerCoords = blueTeamCoords[player.PlayerName] || redTeamCoords[player.PlayerName];
        player.Route.Waypoints[0].NextTangent = playerCoords || { x: 0.0, y: 0.0 };
        player.Route.Waypoints[0].WorldPosition = playerCoords || { x: 0.0, y: 0.0 };
        player.Route.Waypoints[0].PreviousTangent = playerCoords || { x: 0.0, y: 0.0 };
      });
    }
    else {
      console.log("Creating new scenario from scenarioEditorData", scenarioEditorData);
    }

    scenarioData.Name = name || "";

    console.log("##### Handle Create Scenario", JSON.stringify(scenarioData).length);
    createScenario({
      account_id: state.account.id,
      name: name || "",
      description: description || "",
      play_positions: playPositions || [],
      difficulty: Difficulties.EASY,
      phase_of_play: phaseOfPlay,
      scenario_data: scenarioData,
      pressure_time: 0,
      tags: tags
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!!scenario) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  return (
    <>
      <BybDialog
        open={open}
        name="scenario"
        //@ts-ignore
        title={title ? title : (!!scenario ? "Edit scenario" : "Create")}
        onClose={handleClose}
        dialogActions={
          <>
            {!createScenarioLoading && !updateScenarioLoading ? <>
              <BybButton onClick={handleClose} color="secondary" fullWidth>
                Cancel
              </BybButton>
              <BybButton onClick={handleConfirm} color="primary" fullWidth disabled={!name || !playPositions?.length}>
                {buttonLabel ? buttonLabel : (!!scenario ? "Update" : "Create")}
              </BybButton>
            </> :
              <CircularProgress />}
          </>
        }
      >
        <Stack spacing={2}>
          <BybTextField
            label="Name"
            placeholder="The name of your scenario"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={createScenarioLoading || updateScenarioLoading}
          />

          {!scenario && !scenarioEditorData && (
            <Stack direction="row" spacing={2}>
              <Box sx={{ width: "100%" }}>
                <BybTextField
                  label="Your Formation"
                  value={formationYour}
                  fullWidth
                  select
                  required
                  disabled={createScenarioLoading || updateScenarioLoading}
                  onChange={(e) => {
                    setBlueTeamCoords(getCoordinatesForTeam(e.target.value));
                    setFormationYour(e.target.value);
                  }}
                >
                  {Object.values(Formations).map((value, i) => (
                    <MenuItem key={i} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </BybTextField>
              </Box>
              <Box sx={{ width: "100%" }}>
                <BybTextField
                  label="Opponent Formation"
                  value={formationOpponent}
                  fullWidth
                  select
                  required
                  disabled={createScenarioLoading || updateScenarioLoading}
                  onChange={(e) => {
                    setRedTeamCoords(getCoordinatesForTeam(e.target.value, "2"));
                    setFormationOpponent(e.target.value);
                  }}
                >
                  {Object.values(Formations).map((value, i) => (
                    <MenuItem key={i} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </BybTextField>
              </Box>
            </Stack>
          )}

          <BybTextField
            label="Description"
            placeholder="A short description"
            fullWidth
            multiline
            rows={4}
            value={description}
            disabled={createScenarioLoading || updateScenarioLoading}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Stack direction="row" spacing={2}>
            <BybSelectTextField
              label="Field Positions"
              value={getPlayPositionString(playPositions || [])}
              placeholder="Select at least one"
              fullWidth
              required
              disabled={createScenarioLoading || updateScenarioLoading}
              onClick={() => setPlayPositionSelectOpen(true)}
            />
            <Box sx={{ width: "100%" }}>
              <BybTextField
                label="Phase of Play"
                value={phaseOfPlay}
                placeholder="Select"
                fullWidth
                select
                required
                disabled={createScenarioLoading || updateScenarioLoading}
                onChange={(e) => {
                  setPhaseOfPlay(e.target.value as PhaseOfPlay);
                }}
              >
                {Object.values(PhaseOfPlay).map((value, i) => (
                  <MenuItem key={i} value={value}>
                    {PhaseOfPlayMap[value]}
                  </MenuItem>
                ))}
              </BybTextField>
            </Box>
          </Stack>

          <Autocomplete
            multiple
            id="tags"
            options={["GENERAL"]}
            getOptionLabel={(option) => option}
            disabled={createScenarioLoading || updateScenarioLoading}
            value={tags}
            onChange={(e, v) => setTags(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Tags"
                sx={{ position: 'relative' }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  size='small'
                  color='info'
                  sx={{ color: 'black', fontWeight: '600', backgroundColor: getDeterministicColor(option) }}
                  label={option}
                  {...getTagProps({ index })}
                />
              ))}
          />

          <BybSwitch 
            label="Published" 
            checked={published} 
            onChange={() => setPublished(!published)} 
            disabled={createScenarioLoading || updateScenarioLoading}
          />
        </Stack>
      </BybDialog>

      <ScenarioPositionsDialog
        playPositions={playPositions}
        open={playPositionSelectOpen}
        onApply={(playPositions: PlayPositions[]) => setPlayPositions(playPositions)}
        onClose={() => setPlayPositionSelectOpen(false)}
      />
    </>
  );
};

export default ScenarioEditDialog;
